import {
  adminSupportBoardSchema,
  adminSupportListSchema,
  adminSupportSavedRepliesSchema,
  adminSupportSavedReplySchema,
  adminSupportStatusesSchema,
  adminSupportStatusSchema,
  adminSupportTagSchema,
  adminSupportTicketSchema,
  type AdminSupportBoard,
  type AdminSupportList,
  type AdminSupportMoveRequest,
  type AdminSupportReplyRequest,
  type AdminSupportSavedReply,
  type AdminSupportSavedReplyCreate,
  type AdminSupportSavedReplyUpdate,
  type AdminSupportStatus,
  type AdminSupportStatusCreate,
  type AdminSupportStatusUpdate,
  type AdminSupportTag,
  type AdminSupportTagCreate,
  type AdminSupportTagUpdate,
  type AdminSupportTicket,
  type AdminSupportTicketUpdate,
  adminBillingOverviewSchema,
  adminCostsSchema,
  type AdminCosts,
  adminBillingSchema,
  billingEventsResponseSchema,
  type AdminBilling,
  type AdminBillingOverview,
  type BillingEventKind,
  adminBusinessDetailSchema,
  adminBusinessesResponseSchema,
  adminDemoClubSchema,
  adminDemoClubsResponseSchema,
  adminHealthResponseSchema,
  adminSignupSourceSchema,
  type AdminSignupSource,
  deleteBusinessResponseSchema,
  platformSettingsResponseSchema,
  type PlatformSettingsResponse,
  adminRunsOnTeamnaSchema,
  type AdminRunsOnTeamna,
  adminAiAssistantSchema,
  adminAssistantConversationSchema,
  adminAssistantReviewSchema,
  type AdminAiAssistant,
  type AdminAssistantConversation,
  type AdminAssistantReview,
  type AdminAssistantReviewQuery,
  sendDemoLinkResponseSchema,
  staffMeResponseSchema,
  type AdminBusinessDetail,
  type AdminBusinessSummary,
  type AdminDemoClub,
  type AdminHealthResponse,
  type DeleteBusinessResponse,
  type DemoRole,
  type DemoSport,
  type SendDemoLinkResponse,
  type StaffMeResponse,
  adminEarlyAccessResponseSchema,
  adminSignUpAllowanceSchema,
  type AdminEarlyAccessResponse,
  type AdminSignUpAllowance,
  type AllowSignUpRequest,
} from "@teamna/shared";
import { useMutation, useQuery, useQueryClient, type QueryClient, type UseMutationResult, type UseQueryResult } from "@tanstack/react-query";
import { API_BASE_PATH } from "@teamna/shared";
import { apiOrigin } from "@teamna/web-client/api/base-url.ts";
import { createApiClient, newIdempotencyKey } from "@teamna/web-client/api/client.ts";
import { queryKeys } from "@teamna/web-client/hooks/keys.ts";

/**
 * The Control Center's own endpoints: the staff session and the read views.
 *
 * A second client from `@teamna/web-client`, pointed at `/v1` and sending the session cookie. The
 * shared `api` client, which every Desk hook uses, is pointed at `/v1/desk` in `main.tsx`.
 */
export const ccApi = createApiClient(() => ({ basePath: API_BASE_PATH, credentials: "include" }));

export const ccKeys = {
  me: ["cc", "me"] as const,
  businesses: ["cc", "admin", "businesses"] as const,
  business: (businessId: string) => ["cc", "admin", "businesses", businessId] as const,
  health: ["cc", "admin", "health"] as const,
  platformSettings: ["cc", "admin", "platform-settings"] as const,
  runsOnTeamna: (businessId: string) => ["cc", "admin", "businesses", businessId, "runs-on-teamna"] as const,
  aiAssistant: (businessId: string) => ["cc", "admin", "businesses", businessId, "ai-assistant"] as const,
  assistantReview: (filters: Record<string, unknown>) => ["cc", "admin", "assistant", "review", filters] as const,
  assistantConversation: (conversationId: string) => ["cc", "admin", "assistant", "conversation", conversationId] as const,
  demos: ["cc", "admin", "demos"] as const,
  earlyAccess: ["cc", "admin", "early-access"] as const,
  billingOverview: ["cc", "admin", "billing", "overview"] as const,
  costs: (month: string) => ["cc", "admin", "costs", month] as const,
  billing: (businessId: string) => ["cc", "admin", "billing", "business", businessId] as const,
  billingEvents: (filters: { kind?: string; businessId?: string }) => ["cc", "admin", "billing", "events", filters] as const,
  billingEventsAll: ["cc", "admin", "billing", "events"] as const,
  support: ["cc", "admin", "support"] as const,
  supportBoard: ["cc", "admin", "support", "board"] as const,
  supportListAll: ["cc", "admin", "support", "list"] as const,
  supportList: (q: string) => ["cc", "admin", "support", "list", q] as const,
  supportTicket: (ticketId: string) => ["cc", "admin", "support", "ticket", ticketId] as const,
  supportSavedReplies: ["cc", "admin", "support", "saved-replies"] as const,
};

/** GET /v1/me: the signed-in staff member. A 401 means the sign-in page. */
export function useStaffMe(): UseQueryResult<StaffMeResponse> {
  return useQuery({
    queryKey: ccKeys.me,
    queryFn: () => ccApi.get("/me", staffMeResponseSchema),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

/** GET /v1/admin/businesses */
export function useAdminBusinesses(): UseQueryResult<AdminBusinessSummary[]> {
  return useQuery({
    queryKey: ccKeys.businesses,
    queryFn: async () => (await ccApi.get("/admin/businesses", adminBusinessesResponseSchema)).items,
    staleTime: 60 * 1000,
  });
}

/** GET /v1/admin/businesses/:id */
export function useAdminBusiness(businessId: string): UseQueryResult<AdminBusinessDetail> {
  return useQuery({
    queryKey: ccKeys.business(businessId),
    queryFn: () => ccApi.get(`/admin/businesses/${businessId}`, adminBusinessDetailSchema),
    staleTime: 60 * 1000,
    // The chat review knows the business only after the chat loads.
    enabled: businessId !== "",
  });
}

/** GET /v1/admin/demos: the live demo clubs with their logins. */
export function useDemoClubs(): UseQueryResult<AdminDemoClub[]> {
  return useQuery({
    queryKey: ccKeys.demos,
    queryFn: async () => (await ccApi.get("/admin/demos", adminDemoClubsResponseSchema)).items,
    staleTime: 30 * 1000,
  });
}

/** After any demo change, the demo list and the Businesses list both read again. */
function useRefreshDemos(): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ccKeys.demos }),
      queryClient.invalidateQueries({ queryKey: ccKeys.businesses }),
    ]);
  };
}

/**
 * POST /v1/admin/businesses/:id/delete. Deletes the business for good. On success the business is
 * dropped from the cache and every list that can show it is fetched again.
 */
export function useDeleteBusiness(): UseMutationResult<DeleteBusinessResponse, Error, { businessId: string; confirmName: string }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ businessId, confirmName }) =>
      ccApi.post(`/admin/businesses/${businessId}/delete`, { confirmName }, deleteBusinessResponseSchema),
    onSuccess: async (_data, { businessId }) => {
      queryClient.removeQueries({ queryKey: ccKeys.business(businessId) });
      queryClient.removeQueries({ queryKey: ccKeys.billing(businessId) });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ccKeys.businesses, exact: true }),
        queryClient.invalidateQueries({ queryKey: ccKeys.demos }),
        queryClient.invalidateQueries({ queryKey: ccKeys.billingOverview }),
      ]);
    },
  });
}

/** POST /v1/admin/demos */
export function useSpawnDemoClub(): UseMutationResult<AdminDemoClub, Error, DemoSport> {
  const refresh = useRefreshDemos();
  return useMutation({
    mutationFn: (sport: DemoSport) => ccApi.post("/admin/demos", { sport }, adminDemoClubSchema),
    onSuccess: refresh,
  });
}

/** POST /v1/admin/demos/:id/extend */
export function useExtendDemoClub(): UseMutationResult<AdminDemoClub, Error, string> {
  const refresh = useRefreshDemos();
  return useMutation({
    mutationFn: (businessId: string) => ccApi.post(`/admin/demos/${businessId}/extend`, {}, adminDemoClubSchema),
    onSuccess: refresh,
  });
}

/** DELETE /v1/admin/demos/:id */
export function useDestroyDemoClub(): UseMutationResult<void, Error, string> {
  const refresh = useRefreshDemos();
  return useMutation({
    mutationFn: (businessId: string) => ccApi.delete(`/admin/demos/${businessId}`),
    onSuccess: refresh,
  });
}

/** POST /v1/admin/demos/:id/links: emails a one-time sign-in link for one login. */
export function useSendDemoLink(): UseMutationResult<
  SendDemoLinkResponse,
  Error,
  { businessId: string; role: DemoRole; to: string }
> {
  return useMutation({
    mutationFn: ({ businessId, role, to }) =>
      ccApi.post(`/admin/demos/${businessId}/links`, { role, to }, sendDemoLinkResponseSchema),
  });
}

/** How often the health page asks again while it is in view. */
export const ADMIN_HEALTH_REFETCH_MS = 30 * 1000;

/** GET /v1/admin/health, every 30 seconds while the window has focus. */
export function useAdminHealth(): UseQueryResult<AdminHealthResponse> {
  return useQuery({
    queryKey: ccKeys.health,
    queryFn: () => ccApi.get("/admin/health", adminHealthResponseSchema),
    staleTime: 0,
    refetchInterval: ADMIN_HEALTH_REFETCH_MS,
    refetchIntervalInBackground: false,
  });
}

/** Where the Google sign-in starts. A full page load, not a fetch: the browser goes to Google. */
export function googleSignInUrl(): string {
  return `${apiOrigin()}${API_BASE_PATH}/auth/google/start`;
}

/** Ends the staff session. The cookie is HttpOnly, so only the API can clear it. */
export async function signOutOfControlCenter(): Promise<void> {
  try {
    await fetch(`${apiOrigin()}${API_BASE_PATH}/auth/logout`, { method: "POST", credentials: "include" });
  } catch {
    // The page goes to sign-in either way; an expired cookie dies by itself.
  }
}

// --- Costs ----------------------------------------------------------------------------

/** GET /v1/admin/costs: the Twilio balance and the cost of each business in a month (`YYYY-MM`). */
export function useAdminCosts(month: string): UseQueryResult<AdminCosts> {
  return useQuery({
    queryKey: ccKeys.costs(month),
    queryFn: () => ccApi.get(`/admin/costs?month=${month}`, adminCostsSchema),
    staleTime: 60 * 1000,
  });
}

// --- Teamna billing (subscription billing spec, section 6.2) ---------------------------

/** GET /v1/admin/billing/overview: the numbers above the businesses list. */
export function useBillingOverview(): UseQueryResult<AdminBillingOverview> {
  return useQuery({
    queryKey: ccKeys.billingOverview,
    queryFn: () => ccApi.get("/admin/billing/overview", adminBillingOverviewSchema),
    staleTime: 60 * 1000,
  });
}

/** GET /v1/admin/businesses/:id/billing: the Billing card. */
export function useAdminBilling(businessId: string): UseQueryResult<AdminBilling> {
  return useQuery({
    queryKey: ccKeys.billing(businessId),
    queryFn: () => ccApi.get(`/admin/businesses/${businessId}/billing`, adminBillingSchema),
    staleTime: 30 * 1000,
  });
}

/** GET /v1/admin/billing/events, one page at a time (`before` is the oldest line already shown). */
export function fetchBillingEvents(filters: { kind?: BillingEventKind; businessId?: string; before?: string }) {
  return ccApi.get("/admin/billing/events", billingEventsResponseSchema, {
    query: {
      ...(filters.kind ? { kind: filters.kind } : {}),
      ...(filters.businessId ? { businessId: filters.businessId } : {}),
      ...(filters.before ? { before: filters.before } : {}),
    },
  });
}

export type BillingActionPath =
  | "trial-end"
  | "discount"
  | "discount/remove"
  | "keep-access"
  | "refund"
  | "pause"
  | "resume"
  | "credit"
  | "move-to-current-price"
  | "pilot";

/** The actions that move money send an Idempotency-Key; the panel keeps one key while it is open. */
const MONEY_ACTIONS = new Set<BillingActionPath>(["discount", "refund", "credit"]);

/**
 * POST /v1/admin/businesses/:id/billing/<action>. The answer is the Billing card after the action,
 * written straight into its cache; the list, the numbers and the feed read again.
 */
export function useBillingAction(
  businessId: string,
): UseMutationResult<AdminBilling, Error, { path: BillingActionPath; body: Record<string, unknown>; idempotencyKey?: string }> {
  const queryClient = useQueryClient();
  const readAgain = () => {
    void queryClient.invalidateQueries({ queryKey: ccKeys.businesses });
    void queryClient.invalidateQueries({ queryKey: ccKeys.billingOverview });
    void queryClient.invalidateQueries({ queryKey: ccKeys.billingEventsAll });
  };
  return useMutation({
    mutationFn: ({ path, body, idempotencyKey }) =>
      ccApi.post(`/admin/businesses/${businessId}/billing/${path}`, body, adminBillingSchema, {
        ...(MONEY_ACTIONS.has(path) ? { idempotencyKey: idempotencyKey ?? newIdempotencyKey() } : {}),
      }),
    onSuccess: (billing) => {
      queryClient.setQueryData(ccKeys.billing(businessId), billing);
      readAgain();
    },
    // The action may have committed before the answer failed (a lost connection, a 500 while the
    // card was read back). Read the card again too, so it shows what the server has now and a
    // second press does not act on an old state (review F5.3).
    onError: () => {
      void queryClient.invalidateQueries({ queryKey: ccKeys.billing(businessId) });
      readAgain();
    },
  });
}

/**
 * POST /v1/admin/businesses/:id/warm-intro: the "Warm intro" tag on or off (task 0.7). The detail
 * gets the new source at once; the list reads again, because its source column changes too.
 */
export function useSetWarmIntroTag(businessId: string): UseMutationResult<AdminSignupSource, Error, boolean> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (on: boolean) => ccApi.post(`/admin/businesses/${businessId}/warm-intro`, { on }, adminSignupSourceSchema),
    onSuccess: (signupSource) => {
      queryClient.setQueryData<AdminBusinessDetail>(ccKeys.business(businessId), (detail) => (detail ? { ...detail, signupSource } : detail));
      void queryClient.invalidateQueries({ queryKey: ccKeys.business(businessId) });
      void queryClient.invalidateQueries({ queryKey: ccKeys.businesses, exact: true });
    },
    // A failed answer does not prove that nothing changed: the change can commit before the
    // connection fails. The list reads again here; the card reads the detail again itself
    // (`confirmWarmIntroTag`), because it must know if that read worked.
    onError: () => {
      void queryClient.invalidateQueries({ queryKey: ccKeys.businesses, exact: true });
    },
  });
}

/**
 * After a failed tag change: reads the business again. True when the saved value is now on screen,
 * false when the read failed too and the screen may show an old value.
 */
export function useConfirmWarmIntroTag(businessId: string): () => Promise<boolean> {
  const queryClient = useQueryClient();
  return async () => {
    try {
      await queryClient.refetchQueries({ queryKey: ccKeys.business(businessId), exact: true }, { throwOnError: true });
      return queryClient.getQueryState(ccKeys.business(businessId))?.status === "success";
    } catch {
      return false;
    }
  };
}

/** GET /v1/admin/platform-settings: the Teamna-wide switches. */
export function usePlatformSettings(): UseQueryResult<PlatformSettingsResponse> {
  return useQuery({
    queryKey: ccKeys.platformSettings,
    queryFn: () => ccApi.get("/admin/platform-settings", platformSettingsResponseSchema),
  });
}

/**
 * After one switch changed: keeps only that switch from the answer, then reads all switches again.
 * The answer of an older request can arrive last, and its copy of the other switch can be old
 * (internal review F2.4).
 */
async function keepSwitch(queryClient: QueryClient, key: keyof PlatformSettingsResponse, data: PlatformSettingsResponse): Promise<void> {
  queryClient.setQueryData<PlatformSettingsResponse>(ccKeys.platformSettings, (old) => (old ? { ...old, [key]: data[key] } : data));
  await queryClient.invalidateQueries({ queryKey: ccKeys.platformSettings });
}

/** POST /v1/admin/platform-settings/card-payments: card payments on or off for all businesses. */
export function useSetCardPayments(): UseMutationResult<PlatformSettingsResponse, Error, boolean> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (on: boolean) => ccApi.post("/admin/platform-settings/card-payments", { on }, platformSettingsResponseSchema),
    onSuccess: (data) => keepSwitch(queryClient, "cardPayments", data),
  });
}

/** POST /v1/admin/platform-settings/owner-sign-up: open to all, or early access only. */
export function useSetOwnerSignUp(): UseMutationResult<PlatformSettingsResponse, Error, boolean> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (on: boolean) => ccApi.post("/admin/platform-settings/owner-sign-up", { on }, platformSettingsResponseSchema),
    onSuccess: async (data) => {
      await keepSwitch(queryClient, "ownerSignUp", data);
      await queryClient.invalidateQueries({ queryKey: ccKeys.earlyAccess });
    },
  });
}

/** GET /v1/admin/early-access: the website forms, newest first, and the allow list. */
export function useEarlyAccess(): UseQueryResult<AdminEarlyAccessResponse> {
  return useQuery({
    queryKey: ccKeys.earlyAccess,
    queryFn: () => ccApi.get("/admin/early-access", adminEarlyAccessResponseSchema),
    staleTime: 30 * 1000,
  });
}

/** POST /v1/admin/early-access/allowances: from a form, or by number or email. */
export function useAllowSignUp(): UseMutationResult<AdminSignUpAllowance, Error, AllowSignUpRequest> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: AllowSignUpRequest) => ccApi.post("/admin/early-access/allowances", body, adminSignUpAllowanceSchema),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ccKeys.earlyAccess }),
  });
}

/** What "Take back" removes: one allow list entry, or every entry of a form. */
export type TakeBackTarget = { allowanceId: string } | { requestId: string };

/** DELETE /v1/admin/early-access/allowances/:id, or /v1/admin/early-access/requests/:id/allowances */
export function useTakeBackSignUp(): UseMutationResult<unknown, Error, TakeBackTarget> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (target: TakeBackTarget) =>
      ccApi.delete(
        "allowanceId" in target
          ? `/admin/early-access/allowances/${target.allowanceId}`
          : `/admin/early-access/requests/${target.requestId}/allowances`,
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ccKeys.earlyAccess }),
  });
}

/** GET /v1/admin/businesses/:id/runs-on-teamna: the "Runs on Teamna" link of one business. */
export function useRunsOnTeamna(businessId: string): UseQueryResult<AdminRunsOnTeamna> {
  return useQuery({
    queryKey: ccKeys.runsOnTeamna(businessId),
    queryFn: () => ccApi.get(`/admin/businesses/${businessId}/runs-on-teamna`, adminRunsOnTeamnaSchema),
  });
}

/** POST /v1/admin/businesses/:id/runs-on-teamna: the link on or off for one business. */
export function useSetRunsOnTeamna(businessId: string): UseMutationResult<AdminRunsOnTeamna, Error, boolean> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (on: boolean) => ccApi.post(`/admin/businesses/${businessId}/runs-on-teamna`, { on }, adminRunsOnTeamnaSchema),
    onSuccess: (data) => {
      queryClient.setQueryData(ccKeys.runsOnTeamna(businessId), data);
      // The audit tab of the business shows the new row.
      void queryClient.invalidateQueries({ queryKey: ccKeys.business(businessId), exact: true });
    },
  });
}

/**
 * GET /v1/admin/businesses/:id/ai-assistant: AI Assistant of one business, bought, given free by
 * Teamna, or off.
 */
export function useAiAssistantAdmin(businessId: string): UseQueryResult<AdminAiAssistant> {
  return useQuery({
    queryKey: ccKeys.aiAssistant(businessId),
    queryFn: () => ccApi.get(`/admin/businesses/${businessId}/ai-assistant`, adminAiAssistantSchema),
  });
}

/**
 * POST /v1/admin/businesses/:id/ai-assistant: give AI Assistant free (`until` null: no last day),
 * or take the grant back (`null`). A package that the business bought does not change.
 */
export function useSetAiAssistantGrant(businessId: string): UseMutationResult<AdminAiAssistant, Error, { until: string | null } | null> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (grant) => ccApi.post(`/admin/businesses/${businessId}/ai-assistant`, { grant }, adminAiAssistantSchema),
    onSuccess: (data) => {
      queryClient.setQueryData(ccKeys.aiAssistant(businessId), data);
      // The audit tab of the business shows the new row.
      void queryClient.invalidateQueries({ queryKey: ccKeys.business(businessId), exact: true });
      // While acting as the business, the owner menu and Home read the package from the business.
      void queryClient.invalidateQueries({ queryKey: queryKeys.businesses });
    },
    // The change can be saved even when the answer did not arrive: read the state again, and stay
    // pending until it is in.
    onError: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ccKeys.aiAssistant(businessId) }),
        queryClient.invalidateQueries({ queryKey: ccKeys.business(businessId), exact: true }),
        queryClient.invalidateQueries({ queryKey: queryKeys.businesses }),
      ]),
  });
}

// --- AI Assistant review (ai-assistant-v1.md, "Review in Control Center") ---------------

/** The filters of the review page, as the API reads them. Empty values are left out. */
export type AssistantReviewFilters = Omit<AdminAssistantReviewQuery, "days" | "before"> & { days: number };

/** GET /v1/admin/assistant/review, one page at a time (`before` is the oldest prompt already shown). */
export function fetchAssistantReview(filters: AssistantReviewFilters & { before?: string }): Promise<AdminAssistantReview> {
  const query: Record<string, string> = {};
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== "") query[key] = String(value);
  }
  return ccApi.get("/admin/assistant/review", adminAssistantReviewSchema, { query });
}

/**
 * GET /v1/admin/assistant/conversations/:id: one chat, read only. Each read writes an audit row with
 * the staff email, so the chat does not read again by itself.
 */
export function useAssistantConversationForStaff(conversationId: string): UseQueryResult<AdminAssistantConversation> {
  return useQuery({
    queryKey: ccKeys.assistantConversation(conversationId),
    queryFn: () => ccApi.get(`/admin/assistant/conversations/${conversationId}`, adminAssistantConversationSchema),
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });
}

// --- support tickets (support-system.md, section 6) -------------------------------------

/** GET /v1/admin/support/board. Polled, so a new ticket shows without a reload. */
export function useSupportBoard(): UseQueryResult<AdminSupportBoard> {
  return useQuery({
    queryKey: ccKeys.supportBoard,
    queryFn: () => ccApi.get("/admin/support/board", adminSupportBoardSchema),
    staleTime: 15 * 1000,
    refetchInterval: 30 * 1000,
  });
}

/** GET /v1/admin/support/tickets/:id */
export function useSupportTicketForStaff(ticketId: string): UseQueryResult<AdminSupportTicket> {
  return useQuery({
    queryKey: ccKeys.supportTicket(ticketId),
    queryFn: () => ccApi.get(`/admin/support/tickets/${ticketId}`, adminSupportTicketSchema),
    refetchInterval: 20 * 1000,
  });
}

function useAfterSupportWrite() {
  const queryClient = useQueryClient();
  return (ticket: AdminSupportTicket) => {
    queryClient.setQueryData(ccKeys.supportTicket(ticket.id), ticket);
    void queryClient.invalidateQueries({ queryKey: ccKeys.supportBoard });
    void queryClient.invalidateQueries({ queryKey: ccKeys.supportListAll });
  };
}

/**
 * GET /v1/admin/support/tickets?q=: the list view. The search runs on the server (it reads every
 * message); the filters run in the browser. The last rows stay on screen while a new search loads.
 */
export function useSupportList(q: string): UseQueryResult<AdminSupportList> {
  return useQuery({
    queryKey: ccKeys.supportList(q),
    queryFn: () => ccApi.get(`/admin/support/tickets${q ? `?q=${encodeURIComponent(q)}` : ""}`, adminSupportListSchema),
    placeholderData: (previous) => previous,
    staleTime: 15 * 1000,
    refetchInterval: 60 * 1000,
  });
}

/** PUT /v1/admin/support/tickets/:id/tags: every label the ticket has after the change. */
export function useSupportTicketTags(ticketId: string): UseMutationResult<AdminSupportTicket, Error, string[]> {
  const after = useAfterSupportWrite();
  return useMutation({
    mutationFn: (tagIds) => ccApi.put(`/admin/support/tickets/${ticketId}/tags`, { tagIds }, adminSupportTicketSchema),
    onSuccess: after,
  });
}

/** A label or a column changes what every support screen paints, so all of them load again. */
function useAfterSupportSettings() {
  const queryClient = useQueryClient();
  return () => void queryClient.invalidateQueries({ queryKey: ccKeys.support });
}

/** POST /v1/admin/support/tags */
export function useCreateSupportTag(): UseMutationResult<AdminSupportTag, Error, AdminSupportTagCreate> {
  const after = useAfterSupportSettings();
  return useMutation({
    mutationFn: (input) => ccApi.post("/admin/support/tags", input, adminSupportTagSchema),
    onSuccess: after,
  });
}

/** PATCH /v1/admin/support/tags/:id: rename, colour, or archive. */
export function useUpdateSupportTag(): UseMutationResult<AdminSupportTag, Error, AdminSupportTagUpdate & { tagId: string }> {
  const after = useAfterSupportSettings();
  return useMutation({
    mutationFn: ({ tagId, ...input }) => ccApi.patch(`/admin/support/tags/${tagId}`, input, adminSupportTagSchema),
    onSuccess: after,
  });
}

/** POST /v1/admin/support/statuses: a new column at the end of the board. */
export function useCreateSupportStatus(): UseMutationResult<AdminSupportStatus, Error, AdminSupportStatusCreate> {
  const after = useAfterSupportSettings();
  return useMutation({
    mutationFn: (input) => ccApi.post("/admin/support/statuses", input, adminSupportStatusSchema),
    onSuccess: after,
  });
}

/** PATCH /v1/admin/support/statuses/:id: name, colour, kind, default, or archive. */
export function useUpdateSupportStatus(): UseMutationResult<AdminSupportStatus, Error, AdminSupportStatusUpdate & { statusId: string }> {
  const after = useAfterSupportSettings();
  return useMutation({
    mutationFn: ({ statusId, ...input }) => ccApi.patch(`/admin/support/statuses/${statusId}`, input, adminSupportStatusSchema),
    onSuccess: after,
    // A 409 can mean the columns changed under the page: load them again either way.
    onError: after,
  });
}

/** PUT /v1/admin/support/statuses/order: every column that is not archived, in the new order. */
export function useReorderSupportStatuses(): UseMutationResult<{ statuses: AdminSupportStatus[] }, Error, string[]> {
  const after = useAfterSupportSettings();
  return useMutation({
    mutationFn: (statusIds) => ccApi.put("/admin/support/statuses/order", { statusIds }, adminSupportStatusesSchema),
    onSuccess: after,
    onError: after,
  });
}

/** PATCH /v1/admin/support/tickets/:id: the team member or the team priority. */
export function useUpdateSupportTicket(ticketId: string): UseMutationResult<AdminSupportTicket, Error, AdminSupportTicketUpdate> {
  const after = useAfterSupportWrite();
  return useMutation({
    mutationFn: (input) => ccApi.patch(`/admin/support/tickets/${ticketId}`, input, adminSupportTicketSchema),
    onSuccess: after,
  });
}

/** GET /v1/admin/support/saved-replies */
export function useSavedReplies(): UseQueryResult<AdminSupportSavedReply[]> {
  return useQuery({
    queryKey: ccKeys.supportSavedReplies,
    queryFn: async () => (await ccApi.get("/admin/support/saved-replies", adminSupportSavedRepliesSchema)).items,
    staleTime: 60 * 1000,
  });
}

function useAfterSavedReplies() {
  const queryClient = useQueryClient();
  return () => void queryClient.invalidateQueries({ queryKey: ccKeys.supportSavedReplies });
}

/** POST /v1/admin/support/saved-replies */
export function useCreateSavedReply(): UseMutationResult<AdminSupportSavedReply, Error, AdminSupportSavedReplyCreate> {
  const after = useAfterSavedReplies();
  return useMutation({
    mutationFn: (input) => ccApi.post("/admin/support/saved-replies", input, adminSupportSavedReplySchema),
    onSuccess: after,
  });
}

/** PATCH /v1/admin/support/saved-replies/:id */
export function useUpdateSavedReply(): UseMutationResult<AdminSupportSavedReply, Error, AdminSupportSavedReplyUpdate & { replyId: string }> {
  const after = useAfterSavedReplies();
  return useMutation({
    mutationFn: ({ replyId, ...input }) => ccApi.patch(`/admin/support/saved-replies/${replyId}`, input, adminSupportSavedReplySchema),
    onSuccess: after,
  });
}

/** DELETE /v1/admin/support/saved-replies/:id */
export function useDeleteSavedReply(): UseMutationResult<void, Error, string> {
  const after = useAfterSavedReplies();
  return useMutation({
    mutationFn: (replyId) => ccApi.delete(`/admin/support/saved-replies/${replyId}`),
    onSuccess: after,
  });
}

/** POST /v1/admin/support/tickets/:id/messages: a reply or a note, and an optional move. */
export function useSupportReply(ticketId: string): UseMutationResult<AdminSupportTicket, Error, AdminSupportReplyRequest> {
  const queryClient = useQueryClient();
  const after = useAfterSupportWrite();
  return useMutation({
    mutationFn: (input) => ccApi.post(`/admin/support/tickets/${ticketId}/messages`, input, adminSupportTicketSchema),
    onSuccess: after,
    // A 409 on "And move to" means the columns changed: load them, so the menu shows the new ones.
    onError: () => void queryClient.invalidateQueries({ queryKey: ccKeys.supportBoard }),
  });
}

/**
 * POST /v1/admin/support/tickets/:id/move. The board moves the card at once and puts it back if
 * the server refuses.
 */
export function useSupportMove(): UseMutationResult<AdminSupportTicket, Error, AdminSupportMoveRequest & { ticketId: string }, { previous: AdminSupportBoard | undefined }> {
  const queryClient = useQueryClient();
  const after = useAfterSupportWrite();
  return useMutation({
    mutationFn: ({ ticketId, ...input }) => ccApi.post(`/admin/support/tickets/${ticketId}/move`, input, adminSupportTicketSchema),
    onMutate: async ({ ticketId, statusId, afterTicketId }) => {
      await queryClient.cancelQueries({ queryKey: ccKeys.supportBoard });
      const previous = queryClient.getQueryData<AdminSupportBoard>(ccKeys.supportBoard);
      if (previous) queryClient.setQueryData(ccKeys.supportBoard, moveLocally(previous, ticketId, statusId, afterTicketId ?? null));
      return { previous };
    },
    onError: (_error, _input, context) => {
      if (context?.previous) queryClient.setQueryData(ccKeys.supportBoard, context.previous);
      // A 409 means another team member changed the board: load it now, not at the next poll.
      void queryClient.invalidateQueries({ queryKey: ccKeys.supportBoard });
    },
    onSuccess: after,
  });
}

/** The board with one card moved, the way the server will rank it: under `afterTicketId`, or at the top. */
export function moveLocally(board: AdminSupportBoard, ticketId: string, statusId: string, afterTicketId: string | null): AdminSupportBoard {
  const card = board.tickets.find((ticket) => ticket.id === ticketId);
  const status = board.statuses.find((row) => row.id === statusId);
  if (!card || !status) return board;
  const column = board.tickets.filter((ticket) => ticket.statusId === statusId && ticket.id !== ticketId).sort((a, b) => a.boardRank - b.boardRank);
  const index = afterTicketId ? column.findIndex((ticket) => ticket.id === afterTicketId) : -1;
  const above = index >= 0 ? column[index] : undefined;
  const below = column[index + 1];
  const boardRank = above ? (below ? (above.boardRank + below.boardRank) / 2 : above.boardRank + 1) : below ? below.boardRank - 1 : 0;
  return {
    ...board,
    tickets: board.tickets.map((ticket) => (ticket.id === ticketId ? { ...ticket, statusId, kind: status.kind, boardRank } : ticket)),
  };
}
